import { QuestionCircleOutlined } from '@ant-design/icons';
import { DateTime } from 'luxon';

import {
  AutopaySettings,
  CompanyFragment,
  LoanBillSettingsType,
  OpenPayoffStatus,
  PortalLoanSnapshot,
  PortalPaymentDueFragment,
  PortalPaymentFragment,
} from '@willow/graphql-iso/src/portal';
import { convertNumberToOrdinal, formatDate, phoneNumberFormat } from '@willow/shared-iso';
import { getNextPaymentDueDate, NamedMemo, paymentAmountFormat, paymentDateFormat } from '@willow/shared-web';
import { OverlayTrigger, Tooltip } from '@willow/shared-web/bootstrap';
import { parseGqlIsoDate, PortalPaymentDueSubStatus, UnpackArray } from '@willow/types-iso';

import { PortalSelectedLoan } from '../../../App';
import { ActiveWorkoutPlanAlert } from './ActiveWorkoutPlanAlert';
import { AlertWrapper } from './AlertWrapper';

export interface Props {
  isAdditionalPayment: boolean;
  company: CompanyFragment;
  totalAmountDue: number;
  totalAmountDueForWorkoutPlan: number | undefined;
  paymentsDue: PortalPaymentDueFragment[];
  processingPayments: PortalPaymentFragment[];
  subStatus?: PortalPaymentDueSubStatus;
  hasOutstandingBalance?: boolean;
  autopaySettings?: PortalLoanSnapshot['autopaySettings'];
  hasInterestReserve: boolean;
  openPayoffStatus?: OpenPayoffStatus;
  autopayEnabledByServicer: boolean;
  activeDelinquentInterestSetting:
    | UnpackArray<PortalSelectedLoan['currentSnapshot']['delinquentInterestSettings']>
    | undefined;
  currentBillSettingsType: LoanBillSettingsType;
  activeWorkoutPlan: PortalSelectedLoan['currentSnapshot']['activeWorkoutPlan'] | undefined; // Followup #8543: allow multiple active workout plans
}

export const PaymentFormAlerts = NamedMemo<Props>(
  'PaymentFormAlerts',
  ({
    isAdditionalPayment,
    company,
    totalAmountDue,
    totalAmountDueForWorkoutPlan,
    paymentsDue,
    processingPayments,
    subStatus,
    hasOutstandingBalance,
    autopaySettings,
    hasInterestReserve,
    openPayoffStatus,
    autopayEnabledByServicer,
    activeDelinquentInterestSetting,
    currentBillSettingsType,
    activeWorkoutPlan,
  }) => {
    const { isPastDue, paymentFail } = subStatus || {};
    const paymentDate = getNextPaymentDueDate(paymentsDue);

    // When builder is in charge of the loan, only show that alert message
    if (currentBillSettingsType === LoanBillSettingsType.Builder) {
      return (
        <>
          <AlertWrapper
            title="Do not make a payment unless instructed"
            subtitle="Builder is currently responsible for payments"
            level="ERROR"
          />
          {/* Builder doesn't make portal payments, but we're going to show this alert anyways */}
          <ActiveWorkoutPlanAlert
            activeWorkoutPlan={activeWorkoutPlan}
            totalAmountDueForWorkoutPlan={totalAmountDueForWorkoutPlan}
          />
        </>
      );
    }

    const firstDelinquentPaymentDue: Date | undefined = activeDelinquentInterestSetting
      ? DateTime.fromJSDate(parseGqlIsoDate(activeDelinquentInterestSetting.startDate))
          .set({ day: 1 })
          .plus({ months: 1 })
          .toJSDate()
      : undefined;

    return (
      <>
        <ActiveWorkoutPlanAlert
          activeWorkoutPlan={activeWorkoutPlan}
          totalAmountDueForWorkoutPlan={totalAmountDueForWorkoutPlan}
        />

        {hasInterestReserve && totalAmountDue > 0 && (
          <AlertWrapper
            title="Remaining balance due"
            subtitle={`Interest reserve does not cover all of the amount due. Please pay the remaining balance of ${paymentAmountFormat(
              totalAmountDue,
            )}`}
            level="ERROR"
          />
        )}
        {isPastDue && !paymentFail && activeWorkoutPlan?.type !== 'FORBEARANCE' && (
          <AlertWrapper title="Account is past due" subtitle="Please pay immediately to avoid fees" level="ERROR" />
        )}

        {paymentFail && (
          <AlertWrapper
            title="Payment failed to process"
            subtitle={
              <>
                Please try again
                {company?.supportPhone && (
                  <>
                    {' '}
                    or call <a href={`tel:${company.supportPhone}`}>{phoneNumberFormat(company.supportPhone)}</a> for
                    help
                  </>
                )}
              </>
            }
            level="ERROR"
          />
        )}
        {hasOutstandingBalance && !isPastDue && !paymentFail && (
          <AlertWrapper
            title="Outstanding balance due"
            subtitle={
              autopaySettings
                ? 'Unpaid balance will be added for auto-payment'
                : `Balance must be paid in full by ${paymentDateFormat(paymentDate, true)}`
            }
            level={isPastDue ? 'ERROR' : 'INFO'}
          />
        )}

        {activeDelinquentInterestSetting && (
          <AlertWrapper
            title="Default interest is charged"
            subtitle={
              <span>
                Daily default interest starts accuring from{' '}
                {formatDate(parseGqlIsoDate(activeDelinquentInterestSetting.startDate), 'M/d/yyyy')}{' '}
                <OverlayTrigger
                  placement="top"
                  trigger={['hover', 'focus']}
                  overlay={
                    <Tooltip>
                      This will be included in the {formatDate(firstDelinquentPaymentDue, 'MMM d, yyyy')} statement and
                      future statements. The amount due changes daily.
                    </Tooltip>
                  }
                >
                  <QuestionCircleOutlined />
                </OverlayTrigger>
              </span>
            }
            level="ERROR"
          />
        )}

        {openPayoffStatus === OpenPayoffStatus.ActiveOutdated && (
          <AlertWrapper
            title="Payoff statement out of date"
            subtitle="Request a new payoff statement to ensure accuracy"
            level="WARNING"
          />
        )}

        {autopayEnabledByServicer &&
          autopaySettings &&
          !isAdditionalPayment &&
          (totalAmountDue > 0 || processingPayments.length > 0) && (
            <AlertWrapper
              title="No Action Required: Withdraw Scheduled"
              subtitle={getMessageFromAutopaySettings(autopaySettings)}
              level="INFO"
            />
          )}
      </>
    );
  },
);

const getMessageFromAutopaySettings = (autopaySettings: AutopaySettings) => {
  if (autopaySettings.__typename === 'UsioAutopaySettings') {
    const { dayOfTheMonth } = autopaySettings;
    if (dayOfTheMonth) {
      const ordinal = convertNumberToOrdinal(dayOfTheMonth);
      return `Payment will be processed in full automatically on the ${ordinal} of the month`;
    }
  }
  return 'Payment will be processed in full automatically';
};
